/**
 * SendComposerModal — the email-like Approve & Send window (client decision
 * 2026-07-22; decluttered to read like a normal email 2026-07-24). The PM
 * sees and can edit exactly what goes out:
 *
 *   - To: the primary gc_email + additional-recipients roster as chips — every
 *     chip shown is a recipient, and × removes it from sight like a normal
 *     email bar (no parked ghost state). Re-adding is typing: the Gmail-style
 *     inline input filters suggestions drawn from the project's contact book
 *     (addresses previous sends actually reached) and the roster, including
 *     roster addresses currently removed; Enter or a click chips one (a
 *     roster address re-activates its check rather than duplicating), a
 *     free-typed valid address chips itself, Backspace on an empty input
 *     drops the last typed chip. Typed addresses are for THIS send only and
 *     never join the roster.
 *   - Cc: the same field directly under To (client request 2026-08-06), from
 *     the same shared machinery (RecipientChipField) — same chips, same
 *     suggestions, same typing rules. It has NO roster: it starts empty on
 *     every send and holds only what the PM put there. The two fields are
 *     disjoint by construction — Cc refuses an address already in To, and
 *     adding one to To takes it out of Cc — which is also the server's rule
 *     (a To address is dropped from CC there, so nobody is addressed twice).
 *     The PM's own copy is the server's business and is not shown here.
 *   - Subject: a single-line input prefilled once per open with the
 *     server-rendered subject. Untouched → the field is omitted from the
 *     payload and the server renders the canonical line itself (REVISED:
 *     prefix and MOD suffix included). Edited → it ships verbatim via
 *     subject_override, whole line, no decorations re-applied.
 *   - Body: ONE textarea prefilled once per open with the server-rendered
 *     letter (the /preview route). Edits ship verbatim via body_override; an
 *     untouched body omits the field so the server renders the letter itself,
 *     byte-identical to the pre-composer email. Recipient toggles after the
 *     prefill deliberately don't re-roll the textarea — the greeting derives
 *     from the configured primary recipient on the server too, so the two
 *     stay consistent either way.
 *   - Attachments: one quiet line (xlsx name + PDF count + vendor-invoice
 *     count, individual filenames in each count's tooltip), plus — only when
 *     there is something to say — a per-daily plan list: which foreman PDFs go
 *     as a REGENERATED copy (and why), and which wanted to but were demoted to
 *     the original because their signature could not be carried across. Never
 *     editable; the server derives the same plan and does not read this back.
 *

 * The modal only COLLECTS the payload; the parent owns the actual send call
 * (toast, close-and-start-next, refetch) via onSend. Shell: fixed header,
 * scrollable middle, opaque footer — the .pdf-modal flex-column pattern, so
 * the footer can never overlap scrolled content.
 *
 * Success beat (2026-08-01): on a landed send the Send button turns green and
 * reads "Sent" for SENT_BEAT_MS before the modal actually leaves the screen.
 * It is presentation only — see the comment on `sentBeat` below for how the
 * signal is derived and why it cannot touch the parent's send sequence.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatDateShort, formatMoneyCents, formatPcoNumber } from '../lib/format';
import {
  MAX_BODY_OVERRIDE_LEN,
  MAX_SUBJECT_OVERRIDE_LEN,
  type RollupPreviewResponse,
} from '@coffman-tm/shared';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { RecipientChipField, useRecipientChipInput } from './RecipientChipField';
import {
  attachmentPlanBadge,
  signatureBlockedCopy,
  summarizeRegeneration,
} from '../lib/regenerationPlan';
import { HelpTip } from './HelpTip';
import { ExpandChevron } from './design';
import {
  VerificationAcks,
  VerificationNotices,
  VerificationProgressCard,
  VerificationSummaryDisclosure,
} from './VerificationPanel';

/** How long the "Sent" confirmation holds on the button before the modal goes. */
const SENT_BEAT_MS = 700;

export interface SendComposerPayload {
  recipients_selected: string[];
  /**
   * Every off-roster address chipped (or typed) for THIS send. Legacy singular
   * form, still sent alongside the array below carrying the FIRST extra: the
   * Worker and Pages deploy from the same push but not at the same instant, so
   * for a minute a new page can talk to an API that only knows the singular
   * field. Sending both makes the one-extra case — the only one that was
   * possible before today — byte-compatible either way, and a two-extra send
   * against a not-yet-deployed API fails loudly (`unknown_recipient`) rather
   * than quietly dropping a recipient.
   */
  recipient_extra?: string;
  /** Every off-roster address for this send. Never joins the roster. */
  recipients_extra?: string[];
  /**
   * The Cc field's addresses for THIS send (2026-08-06). Never joins the
   * roster, never widens the To allow-list. Omitted entirely when the field is
   * empty, so a send with no CC is byte-identical to a pre-Cc-field one.
   */
  recipients_cc?: string[];
  /** Present only when the PM actually edited the prefilled body. */
  body_override?: string;
  /** Present only when the PM actually edited the prefilled subject. Ships
   *  verbatim as the whole Subject line. */
  subject_override?: string;
  /**
   * Verification acknowledgments the PM ticked. The server re-runs the same
   * checks and refuses the send when a required key is missing, so omitting
   * one can only ever block — never wave anything through.
   */
  acks?: string[];
}

export function SendComposerModal({
  open,
  pcoId,
  weekEndingDate,
  rollupId,
  pcoNumber,
  submissionLabel,
  gcLabel,
  invoiceTotalCents,
  primaryEmail,
  rosterEmails,
  busy,
  error,
  footerExtra,
  onSend,
  onCancel,
}: {
  open: boolean;
  pcoId: string;
  weekEndingDate: string;
  /** Canonical rollup id — preview + send target this exact rollup (split-week
   *  safe). null falls back to the legacy (pco, week) routes. */
  rollupId: string | null;
  pcoNumber: string;
  /** "004.13" — the submission this week bills under. Falls back to the bare
   *  PCO number in the modal title when empty. */
  submissionLabel?: string;
  /** GC display label ("Hoffman") for the tooltip copy. */
  gcLabel: string;
  invoiceTotalCents: number;
  /** Configured primary recipient (project.gc_email). */
  primaryEmail: string;
  /** The additional-recipients roster (project.gc_additional_emails). */
  rosterEmails: string[];
  /** True while the parent's send is in flight — locks the whole modal. */
  busy: boolean;
  /** Send error surfaced by the parent (e.g. the pm_email_required message). */
  error: string | null;
  /** Optional extra content (e.g. WeeklyRollupsTab's "close this submission
   *  and start the next" checkbox) — rendered in a bordered callout between
   *  the body and the footer. */
  footerExtra?: ReactNode;
  onSend: (payload: SendComposerPayload) => void;
  onCancel: () => void;
}) {
  // `me` came out with the Project row (2026-08-02). Since 2026-08-13 the
  // subject no longer names the project either (client's exact GC format);
  // pre-send, the project appears only in the letter body's "Project ..." line.
  const { token } = useAuth();

  // Recipient rows = primary + roster, de-duped case-insensitively so an address
  // that appears in both never renders (or sends) twice.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ email: string; isPrimary: boolean }> = [];
    const candidates: Array<{ email: string; isPrimary: boolean }> = [
      ...(primaryEmail ? [{ email: primaryEmail, isPrimary: true }] : []),
      ...rosterEmails.map((email) => ({ email, isPrimary: false })),
    ];
    for (const c of candidates) {
      const key = c.email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }, [primaryEmail, rosterEmails]);

  // A recipient is checked unless explicitly toggled off — so `checks` only
  // records the un-checks. Default-true means a still-loading roster (or its
  // array identity churning between renders) never has to re-seed this map, so
  // the reset effect can key on `open` alone and never wipe the PM's edits.
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  // Off-roster addresses chipped for this send, in the order they were added.
  const [extras, setExtras] = useState<string[]>([]);
  // The Cc field's addresses, in the order they were added. No roster and no
  // default — a Cc is a per-send decision, so it starts empty every time.
  const [cc, setCc] = useState<string[]>([]);
  // The project's contact book — addresses previous sends actually reached.
  // Suggestions only; failing to load it costs nothing but the convenience,
  // so there is no error state and no retry.
  const [contacts, setContacts] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<RollupPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // The prefill contract: the subject input and the body textarea are seeded
  // from the preview exactly ONCE per open. The refs hold the seeded strings
  // (null = not yet seeded) — the dirty checks compare against them, and a
  // later preview response can never clobber the PM's edits.
  const prefillRef = useRef<string | null>(null);
  const subjectPrefillRef = useRef<string | null>(null);

  // ---- To + Cc ------------------------------------------------------------
  // Two instances of one field (RecipientChipField). Everything about the
  // typing — validity, duplicates, suggestions, the keyboard, the popover —
  // lives in the hook; what a chip MEANS stays here, because it is different
  // in each field.
  //
  // Every address currently rendered as a To chip. Roster chips are here only
  // while they are ACTIVE (review fix, 2026-08-02): a removed roster address is
  // NOT chipped — so typing it offers it as a suggestion and never claims
  // "already in the list" about an address the send would exclude.
  const chippedTo = [
    ...rows.filter((r) => checks[r.email] !== false).map((r) => r.email),
    ...extras,
  ];
  // Everything the email reaches, either line. Excluded from BOTH fields'
  // suggestions — an address already on the message is nothing to offer.
  const onMessage = [...chippedTo, ...cc];
  const rosterEmailList = rows.map((r) => r.email);

  const toInput = useRecipientChipInput({
    contacts,
    roster: rosterEmailList,
    onMessage,
    // Duplicate detection is per-field on purpose: an address sitting in Cc is
    // not a duplicate here, it is a candidate to move up to To.
    chipped: chippedTo,
    disabled: busy,
    onCommit: (value) => {
      const rosterRow = rows.find((r) => r.email.toLowerCase() === value.toLowerCase());
      if (rosterRow) {
        // A roster address re-activates its own chip (review fix, 2026-08-02) —
        // typing it means "include them", so the ghost turns solid rather than
        // the address doubling as an extra.
        setChecks((prev) => ({ ...prev, [rosterRow.email]: true }));
      } else if (!chippedTo.some((e) => e.toLowerCase() === value.toLowerCase())) {
        setExtras((prev) => [...prev, value]);
      }
      // TO WINS. Moving an address up here takes it out of Cc, so the two
      // fields stay disjoint — which is the server's rule too (a To address is
      // dropped from the CC header), and the only way the screen can agree with
      // the send about who gets addressed and who gets copied.
      setCc((prev) => prev.filter((e) => e.toLowerCase() !== value.toLowerCase()));
    },
    // Gmail's behavior, scoped to the chips this field created. Roster chips
    // are deliberately out of reach: they are toggled by clicking, and a stray
    // backspace silently dropping the GC's primary address off the week's bill
    // is not a trade worth making for the keystroke.
    canBackspace: extras.length > 0,
    onBackspaceEmpty: () => setExtras((prev) => prev.slice(0, -1)),
  });

  const ccInput = useRecipientChipInput({
    contacts,
    roster: rosterEmailList,
    onMessage,
    // Cc's duplicate set is the WHOLE message: an address already addressed in
    // To is not something to also copy, and saying "already in the list" about
    // it is exactly true.
    chipped: onMessage,
    disabled: busy,
    onCommit: (value) => {
      setCc((prev) =>
        prev.some((e) => e.toLowerCase() === value.toLowerCase()) ||
        chippedTo.some((e) => e.toLowerCase() === value.toLowerCase())
          ? prev
          : [...prev, value],
      );
    },
    // Every Cc chip was typed or picked this send, so all of them are in reach.
    canBackspace: cc.length > 0,
    onBackspaceEmpty: () => setCc((prev) => prev.slice(0, -1)),
  });

  // Pulled out because the per-open reset effect below lists them as
  // dependencies; both are stable, so the effect still fires only on open.
  const { reset: resetToInput } = toInput;
  const { reset: resetCcInput } = ccInput;

  // ---- Success beat -------------------------------------------------------
  // Presentation only. The parent owns the send call, so the only success
  // signal this modal can observe is the parent CLOSING it: on a landed send
  // `open` flips false while `busy` is still true (the parent clears busy only
  // after its refetch), and on a failed send the modal stays open with `error`
  // rendered. Every other close path — Cancel, the X, Esc, the overlay — is
  // already blocked while busy, so that transition can only mean the send
  // landed. No failure path can reach it.
  //
  // The beat holds the modal on screen for SENT_BEAT_MS AFTER the parent has
  // closed it. It does not delay, gate, reorder or observe anything the parent
  // does: toast, close, and refetch run byte-identically and at exactly the
  // same moment as before, underneath the held overlay (the toast viewport is
  // z-index 200, above the modal's 100, so it still lands in front).
  const [sentBeat, setSentBeat] = useState(false);
  // Regeneration plan rows fold behind their count (client, 2026-08-01).
  const [planOpen, setPlanOpen] = useState(false);
  // Verification acknowledgments ticked in the panel. Reset with every open —
  // an acknowledgment is about THIS week's numbers and must never carry across.
  const [acks, setAcks] = useState<Set<string>>(() => new Set());
  // THE THREE ACTS. 'verifying' shows only the progress card; 'composing' is
  // the email form; 'blocked' keeps the card on its failing line and never
  // reaches the form. A preview that returns no verification at all (an API
  // older than the feature) goes straight to 'composing' — an old Worker has no
  // server-side gate either, so the window behaves exactly as it did before.
  const [act, setAct] = useState<'verifying' | 'composing' | 'blocked'>('verifying');
  // How long the preview took. The card's ring has been creeping for exactly
  // that stretch, so this is where the staged reveal picks it up. It no longer
  // shortens the staging: that discount was priced on a ring which was not
  // actually filling during the fetch, and the beat is now the same length
  // whatever the server took (2026-08-06).
  const [previewElapsedMs, setPreviewElapsedMs] = useState(0);
  const sendInFlightRef = useRef(false);
  const beatTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset every field each time the modal opens — and if the send target
  // changes while mounted, so a stale prefill can never carry across rollups.
  useEffect(() => {
    if (!open) return;
    // Reopened during a beat (or retargeted mid-beat): drop it immediately so
    // a stale timer can never green out a fresh composer.
    if (beatTimerRef.current !== null) {
      clearTimeout(beatTimerRef.current);
      beatTimerRef.current = null;
    }
    setSentBeat(false);
    sendInFlightRef.current = false;
    setChecks({});
    setExtras([]);
    setCc([]);
    resetToInput();
    resetCcInput();
    setSubject('');
    setBody('');
    setPreview(null);
    setPreviewError(null);
    setAcks(new Set());
    setAct('verifying');
    setPreviewElapsedMs(0);
    prefillRef.current = null;
    subjectPrefillRef.current = null;
    // The two resets are stable for the component's life (useCallback with no
    // deps), so listing them keeps the dependency array honest without
    // re-running this on every render — which would wipe the PM's edits.
  }, [open, rollupId, pcoId, weekEndingDate, resetToInput, resetCcInput]);

  // Gated on "is the overlay on screen", which through a send is one unbroken
  // true → one acquire, one release. Not a busy flag: it never churns mid-life,
  // and without the beat term the scrollbar would snap back under a still-
  // visible overlay.
  useBodyScrollLock(open || sentBeat);

  // Arm the beat on the parent's success close; disarm on unmount so a modal
  // torn down mid-beat never calls setState on a dead component.
  useEffect(() => {
    if (open) {
      if (busy) sendInFlightRef.current = true;
      return;
    }
    if (!sendInFlightRef.current) return;
    sendInFlightRef.current = false;
    setSentBeat(true);
    beatTimerRef.current = setTimeout(() => {
      beatTimerRef.current = null;
      setSentBeat(false);
    }, SENT_BEAT_MS);
  }, [open, busy]);

  useEffect(
    () => () => {
      if (beatTimerRef.current !== null) clearTimeout(beatTimerRef.current);
    },
    [],
  );

  // Esc closes — with a discard confirm when the body carries unsent edits.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || busy) return;
      const dirty =
        (prefillRef.current !== null && body !== prefillRef.current && body.trim() !== '') ||
        (subjectPrefillRef.current !== null &&
          subject !== subjectPrefillRef.current &&
          subject.trim() !== '');
      if (dirty && !window.confirm('Discard your edits to the message?')) return;
      onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, body, subject, onCancel]);

  // Fetch the server-rendered preview once per open, then seed the textarea.
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      const startedAt = performance.now();
      try {
        const p = rollupId
          ? await api.rollups.previewById(token, rollupId)
          : await api.rollups.preview(token, pcoId, weekEndingDate);
        if (cancelled) return;
        setPreviewElapsedMs(performance.now() - startedAt);
        setPreview(p);
        // No verification on the wire → nothing to stage, go straight to the
        // form (deploy skew: Pages can briefly outrun the Worker).
        if (!p.verification) setAct('composing');
        if (prefillRef.current === null) {
          prefillRef.current = p.text_body;
          setBody(p.text_body);
        }
        if (subjectPrefillRef.current === null) {
          subjectPrefillRef.current = p.subject;
          setSubject(p.subject);
        }
      } catch (e) {
        if (!cancelled) {
          setPreviewElapsedMs(performance.now() - startedAt);
          setPreviewError(e instanceof Error ? e.message : 'Could not load preview');
          // The preview itself failed — there is no verification to stage and
          // no gate to enforce here. Show the form so the error is readable in
          // context; the SERVER still refuses anything it should.
          setAct('composing');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, rollupId, pcoId, weekEndingDate]);

  // Load the contact book once per open. Best-effort: a failure leaves the
  // suggestion pool as the roster alone, which is exactly how the field
  // behaved before contacts existed, so there is nothing to report.
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.rollups.sendContacts(token);
        if (!cancelled) setContacts(res.contacts.map((c) => c.email));
      } catch {
        if (!cancelled) setContacts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token]);

  if (!open && !sentBeat) return null;

  // Locked shell. `busy` covers the send itself; `sentBeat` covers the window
  // after the parent has already closed us, where busy may have gone false
  // again while the overlay is still up — a double-click in there must not
  // reach onCancel or re-arm Send.
  const inert = busy || sentBeat;

  // ---- What actually gets sent --------------------------------------------
  const checkedEmails = rows.filter((r) => checks[r.email] !== false).map((r) => r.email);
  // A valid address still sitting in an input counts as a recipient WITHOUT
  // being committed to a chip first (see RecipientChipInput.pendingTyped).
  const extraRecipients = [...extras, ...(toInput.pendingTyped ? [toInput.pendingTyped] : [])];
  const recipientsSelected = [...checkedEmails, ...extraRecipients];
  // TO WINS, on the payload as well as in the field. Committing an address to
  // To already pulls it out of Cc, so this only catches the sliver where an
  // exact Cc'd address is sitting half-typed in the To input — and the server
  // applies the identical rule, so the request can never ask for one address on
  // both lines.
  const ccRecipients = [...cc, ...(ccInput.pendingTyped ? [ccInput.pendingTyped] : [])].filter(
    (e) => !recipientsSelected.some((r) => r.toLowerCase() === e.toLowerCase()),
  );
  // Prefill landed but the PM blanked the field: the screen would show empty
  // while the server (receiving no override) would send the full standard
  // letter/subject. The screen and the send must never disagree — block Send
  // in that state instead.
  const bodyBlank = prefillRef.current !== null && body.trim() === '';
  const subjectBlank = subjectPrefillRef.current !== null && subject.trim() === '';

  // ---- Verification gate --------------------------------------------------
  // The server enforces this independently — it re-runs every check before it
  // stores, claims or sends anything, and refuses with a 422/428. This is the
  // same verdict shown early so the PM is never surprised by a refusal after
  // clicking Send.
  //
  // A preview that carries NO `verification` field is an API that predates this
  // feature (Pages and the Worker deploy from one push but not one instant).
  // That case is deliberately not gated: an older Worker has no server-side
  // gate either, so the window behaves exactly as it did before — whereas
  // treating a missing field as "still checking" would leave Send dead forever.
  const verification = preview?.verification ?? null;
  const verificationPending = previewLoading;
  const verificationFailed = verification ? !verification.passed : false;
  // Act 1 and Act 3 render no Send button at all, but keep the gate honest at
  // the predicate level too rather than relying on the button not existing.
  const inComposer = act === 'composing';
  const unmetAcks = verification
    ? verification.required_acks.filter((a) => !acks.has(a.key)).length
    : 0;
  const canSend =
    recipientsSelected.length > 0 &&
    // Half-typed junk in EITHER field blocks the send — the Cc line is as
    // much a part of the message as the To line.
    !toInput.typedShowsError &&
    !ccInput.typedShowsError &&
    !inert &&
    !bodyBlank &&
    !subjectBlank &&
    !verificationPending &&
    !verificationFailed &&
    unmetAcks === 0 &&
    inComposer;

  // Only ship an override when the PM actually changed the prefilled text (and
  // didn't blank it — an all-whitespace value falls back to the rendered
  // letter/subject server-side anyway, so don't pretend it's an edit).
  const bodyDirty =
    prefillRef.current !== null && body !== prefillRef.current && body.trim() !== '';
  const subjectDirty =
    subjectPrefillRef.current !== null &&
    subject !== subjectPrefillRef.current &&
    subject.trim() !== '';

  const doSend = () => {
    if (!canSend) return;
    onSend({
      recipients_selected: recipientsSelected,
      // Both forms — see SendComposerPayload.recipient_extra for why the
      // singular still rides along.
      ...(extraRecipients.length > 0
        ? { recipient_extra: extraRecipients[0], recipients_extra: extraRecipients }
        : {}),
      // Omitted entirely when the Cc field is empty, so a send without one is
      // byte-identical to every send before the field existed.
      ...(ccRecipients.length > 0 ? { recipients_cc: ccRecipients } : {}),
      ...(subjectDirty ? { subject_override: subject } : {}),
      ...(bodyDirty ? { body_override: body } : {}),
      ...(acks.size > 0 ? { acks: [...acks] } : {}),
    });
  };

  const xlsxAttachment = preview?.attachments.find((a) => a.kind === 'xlsx') ?? null;
  const pdfAttachments = preview?.attachments.filter((a) => a.kind === 'pdf') ?? [];
  const invoiceAttachments = preview?.attachments.filter((a) => a.kind === 'invoice') ?? [];

  // Per-daily plan rows — ONLY the dailies whose attachment is not the plain
  // original. A week where nothing was corrected renders no list at all and the
  // modal looks exactly as it did before regeneration shipped.
  const planRows = pdfAttachments
    .map((a) => ({ filename: a.filename, badge: attachmentPlanBadge(a.regeneration) }))
    .filter((r): r is { filename: string; badge: NonNullable<typeof r.badge> } => r.badge !== null);
  const planSummary = summarizeRegeneration(preview?.attachments ?? []);
  // Branched on the actual trigger: `signature_blocked` fires on corrections,
  // scoping, or code normalization, and the consequence of each is different —
  // for a scoped daily the original still shows the OTHER PCO's work.
  const blockedCopy = signatureBlockedCopy(planSummary);

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (inert) return;
        if ((bodyDirty || subjectDirty) && !window.confirm('Discard your edits to the message?'))
          return;
        onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Review and send"
    >
      <div
        className="new-pco-modal send-composer"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 820 }}
      >
        <div className="pdf-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <div className="pdf-modal-title">
              {/* "WE" — the crew's own shorthand for week ending. */}
              Send PCO {submissionLabel || formatPcoNumber(pcoNumber)} · WE{' '}
              {formatDateShort(weekEndingDate)}
            </div>
            <HelpTip title="What sending does" learnMore="approve-and-send">
              Sends a real email to {gcLabel} with the workbook and source PDFs attached, then
              locks the rollup. You can reopen afterward if {gcLabel} flags an issue.
            </HelpTip>
          </div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onCancel}
            disabled={inert}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>

        {/* ---------- ACT 1 / ACT 3 — the verification card, alone ----------
            Opening the send window shows only this. It works through the real
            checks, resolves on "The math checks out", holds a beat, and hands
            over to the composer below. A failure stops it on the failing line
            and never hands over — no form, nothing sent, week untouched. */}
        {act !== 'composing' && (
          <div className="new-pco-body composer-body verify-act">
            <VerificationProgressCard
              verification={verification}
              loading={previewLoading && !preview}
              elapsedMs={previewElapsedMs}
              onResolved={() => setAct(verificationFailed ? 'blocked' : 'composing')}
            />
          </div>
        )}

        {act !== 'composing' && (
          <div className="composer-footer">
            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={inert}>
              {act === 'blocked' ? 'Close' : 'Cancel'}
            </button>
          </div>
        )}

        {act === 'composing' && (
        <div className="new-pco-body composer-body">
          {/* ---------- To ----------
              Gmail-style chip field (client, 2026-08-02). Roster chips carry
              the same checks state the checkbox rows did, but a removed
              address is GONE from the field, not parked as a ghost (client
              revision, same day: "when I remove an address it should be
              gone from sight, like a normal email bar"). Re-adding is the
              normal path: type it — suggestions surface roster addresses
              that aren't currently included, and the commit re-activates the
              roster check rather than duplicating them as an extra. No
              PRIMARY tag — the configured primary simply leads the row.
              After roster chips come the addresses typed for THIS send,
              then the inline input. */}
          <RecipientChipField
            label="To"
            idSuffix="to"
            chips={chippedTo}
            onRemoveChip={(email) => {
              const rosterRow = rows.find((r) => r.email === email);
              if (rosterRow) setChecks((prev) => ({ ...prev, [rosterRow.email]: false }));
              else setExtras((prev) => prev.filter((e) => e !== email));
            }}
            input={toInput}
            placeholder={rows.length === 0 ? 'name@company.com' : 'Add someone…'}
            addAriaLabel="Add a recipient for this send"
            note={
              rows.length === 0 ? (
                <div className="composer-empty">
                  No GC email is configured. Add one in Project Settings before sending.
                </div>
              ) : undefined
            }
            hint={
              <div className="composer-hint composer-to-hint">
                {/* The pendingTyped line is load-bearing honesty (review,
                    2026-08-02): a valid address still in the input IS sent, so
                    the hint must say so — not imply Enter is required. */}
                {toInput.typedShowsError
                  ? 'Enter a valid email address, or clear it.'
                  : toInput.typedIsDuplicate
                    ? 'Already in the list — no need to add it again.'
                    : toInput.pendingTyped
                      ? `${toInput.pendingTyped} will be included in this send.`
                      : 'Type an address and press Enter to add it to this send.'}
                <HelpTip title="Remembered for next time">
                  An address you type here is not added to the project&apos;s saved recipients — it
                  goes on this send only. It will show up as a suggestion the next time you send,
                  because the portal remembers who you have emailed.
                </HelpTip>
              </div>
            }
          />

          {/* ---------- Cc ----------
              The same field, one line down (client request 2026-08-06). No
              roster and no default: a copy is a per-send decision, so it opens
              empty every time and renders as the labeled empty input row. An
              address already on the To line is refused rather than copied
              twice — and the server drops any that slips through, so the two
              lines can never disagree about who was addressed. */}
          <RecipientChipField
            label="Cc"
            idSuffix="cc"
            chips={cc}
            onRemoveChip={(email) => setCc((prev) => prev.filter((e) => e !== email))}
            input={ccInput}
            placeholder="Copy someone…"
            addAriaLabel="Add a Cc recipient for this send"
            hint={
              <div className="composer-hint composer-to-hint">
                {ccInput.typedShowsError
                  ? 'Enter a valid email address, or clear it.'
                  : ccInput.typedIsDuplicate
                    ? 'Already in the list — no need to add it again.'
                    : ccInput.pendingTyped
                      ? `${ccInput.pendingTyped} will be copied on this send.`
                      : 'Copy someone on this send only — nobody here is saved to the project.'}
              </div>
            }
          />

          {/* Project line removed (client, 2026-08-02) so the form reads
              To / Subject / Message like a real email. The subject stopped
              naming the project on 2026-08-13 (client's exact GC format), so
              the wrong-project tripwire the subject briefly carried is gone —
              the letter body's "Project <number> (<name>)" line is the one
              pre-send surface that still names it (known residual, recorded
              in docs/system/07-approve-and-send.md). */}

          {/* ---------- Subject (editable) ----------
              Same contract as the body: prefilled once from the preview,
              untouched means the field is omitted and the server renders the
              canonical line, edited means it ships verbatim — whole line,
              REVISED: prefix, MOD# and description tail included, nothing
              re-applied on top. The REVISED badge stays beside the label as
              the preview's read of this week, not as a promise about the
              text. */}
          <div className="composer-section">
            <div className="composer-section-label">
              Subject
              {preview?.is_revision && <span className="composer-revised-tag">REVISED</span>}
            </div>
            <input
              type="text"
              className="composer-textarea composer-subject-input"
              value={subject}
              disabled={busy || subjectPrefillRef.current === null}
              maxLength={MAX_SUBJECT_OVERRIDE_LEN}
              placeholder={previewLoading ? 'Loading…' : ''}
              onChange={(e) => setSubject(e.target.value)}
              aria-label="Email subject"
            />
            {subjectBlank && (
              <div className="composer-hint" style={{ color: 'var(--danger)' }}>
                Subject is empty — write one, or close and reopen to restore the standard
                subject.
              </div>
            )}
          </div>

          {/* ---------- Body (editable) ---------- */}
          <div className="composer-section">
            <div className="composer-section-label">Message</div>
            {previewError ? (
              <div className="composer-error">
                Couldn&apos;t load the email preview. {previewError}
              </div>
            ) : (
              <>
                <textarea
                  className="composer-textarea composer-body"
                  value={body}
                  disabled={busy || prefillRef.current === null}
                  maxLength={MAX_BODY_OVERRIDE_LEN}
                  rows={12}
                  placeholder={previewLoading ? 'Loading…' : ''}
                  onChange={(e) => setBody(e.target.value)}
                  aria-label="Email message"
                />
                {bodyBlank && (
                  <div className="composer-hint" style={{ color: 'var(--danger)' }}>
                    Message is empty — write a message or close and reopen to restore the
                    standard letter.
                  </div>
                )}
              </>
            )}
          </div>

          {/* ---------- Attachments (read-only) ---------- */}
          {preview && xlsxAttachment && (
            <div className="composer-attach-line">
              <span className="composer-section-label">Attachments</span>{' '}
              <span className="mono">{xlsxAttachment.filename}</span>
              {pdfAttachments.length > 0 && (
                <>
                  {' + '}
                  <span
                    className="composer-attach-pdfs"
                    title={pdfAttachments.map((a) => a.filename).join('\n')}
                  >
                    {pdfAttachments.length} foreman PDF
                    {pdfAttachments.length === 1 ? '' : 's'}
                  </span>
                </>
              )}
              {invoiceAttachments.length > 0 && (
                <>
                  {' + '}
                  <span
                    className="composer-attach-pdfs"
                    title={invoiceAttachments.map((a) => a.filename).join('\n')}
                  >
                    {invoiceAttachments.length} vendor invoice
                    {invoiceAttachments.length === 1 ? '' : 's'}
                  </span>
                </>
              )}
            </div>
          )}

          {/* ---------- Per-daily plan (read-only) ----------
              What the GC actually gets for each foreman PDF. The filename is
              identical whichever copy goes, so this list is the only thing on
              screen that says which one it is. */}
          {planRows.length > 0 && (
            <div className="composer-plan-list">
              {/* Folded to a one-line count by default (client, 2026-08-01):
                  the disclosure survives — "which PDFs did we alter before
                  they go to the GC?" opens on demand — without the row list
                  filling the composer. The signature warning below NEVER
                  hides behind this toggle. */}
              <button
                type="button"
                className="btn-link"
                style={{ fontSize: 12, alignSelf: 'flex-start' }}
                aria-expanded={planOpen}
                onClick={() => setPlanOpen((v) => !v)}
              >
                {planRows.length} regenerated PDF{planRows.length === 1 ? '' : 's'}
                <ExpandChevron expanded={planOpen} size={12} />
              </button>
              {planOpen &&
                planRows.map((row) => (
                  <div key={row.filename} className="composer-plan-row">
                    <span className="mono composer-plan-name">{row.filename}</span>
                    <span className={`composer-plan-badge ${row.badge.tone}`}>
                      {row.badge.label}
                    </span>
                  </div>
                ))}
            </div>
          )}

          {blockedCopy.length > 0 && (
            <div className="composer-plan-warning">
              {blockedCopy.map((line) => (
                <p key={line} className="composer-plan-warning-line">
                  {line}
                </p>
              ))}
            </div>
          )}

          {/* ---------- Hand-added materials (Act 2a′) ----------
              Shown, never asked. Materials bill off vendor paperwork the
              portal never receives, so printing them where the PM reads the
              email is the only honest thing this window can do about them —
              it sits above the acks because that is where its own checkbox
              used to be, and the money should be read before the questions. */}
          <VerificationNotices verification={verification} />

          {/* ---------- Acknowledgments (Act 2a) ----------
              These GATE Send, so they stay in the body where the PM cannot
              miss them — never folded into the summary below. Each is a
              question only a human can answer: today, a daily a second model
              read differently. */}
          <VerificationAcks
            verification={verification}
            acks={acks}
            disabled={inert}
            onToggleAck={(key, on) =>
              setAcks((prev) => {
                const next = new Set(prev);
                if (on) next.add(key);
                else next.delete(key);
                return next;
              })
            }
          />

          {footerExtra && <div className="composer-carry">{footerExtra}</div>}

          {error && <div className="composer-error">{error}</div>}

          {/* ---------- The receipt, folded away (Act 2b) ----------
              He watched these resolve a moment ago in Act 1; keeping them
              expanded would push the actual email off the screen. Collapsed,
              re-openable, and it never holds an acknowledgment. */}
          <VerificationSummaryDisclosure verification={verification} />
        </div>
        )}

        {act === 'composing' && (
        <div className="composer-footer">
          <div className="composer-total">
            <span className="muted">Invoice total</span>{' '}
            <span className="mono" style={{ fontWeight: 600 }}>
              {formatMoneyCents(invoiceTotalCents)}
            </span>
          </div>
          <span
            className="composer-send-count"
            style={recipientsSelected.length === 0 ? { color: 'var(--danger)' } : undefined}
          >
            {recipientsSelected.length === 0
              ? 'No recipients selected'
              : `Send to ${recipientsSelected.length} recipient${recipientsSelected.length === 1 ? '' : 's'}`}
          </span>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={inert}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn btn-cta${sentBeat ? ' composer-send-success' : ''}`}
            onClick={doSend}
            disabled={!canSend}
          >
            {sentBeat ? (
              <>
                <Check size={14} strokeWidth={2.5} aria-hidden />
                Sent
              </>
            ) : busy ? (
              'Sending…'
            ) : (
              'Send'
            )}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
